import React, { useState } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

const Transfer: React.FC = () => {
  const colors = getThemeColors('dark');

  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [transferType, setTransferType] = useState('phone');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const availableBalance = 25000; // Mock balance

  const transferTypes = [
    { id: 'phone', name: 'Phone Number', icon: '📱', placeholder: 'Enter phone number' },
    { id: 'email', name: 'Email', icon: '📧', placeholder: 'Enter email address' },
    { id: 'user', name: 'Username', icon: '👤', placeholder: 'Enter username' },
  ];

  const handleTransfer = async () => {
    const transferAmount = parseFloat(amount) || 0;

    if (!transferAmount || transferAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (transferAmount > availableBalance) {
      alert('Insufficient balance');
      return;
    }

    if (!recipient.trim()) {
      alert('Please enter a recipient');
      return;
    }

    try {
      setLoading(true);
      // Mock API call - in real app this would call actual API
      await new Promise(resolve => setTimeout(resolve, 2000));

      alert(`Transfer of KES ${transferAmount.toLocaleString()} to ${recipient} initiated successfully`);
      setAmount('');
      setRecipient('');
      setNote('');
    } catch (error) {
      console.error('Transfer failed:', error);
      alert('Transfer failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    header: {
      textAlign: 'center',
      marginBottom: spacing.xl,
    },
    title: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    subtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    balanceCard: {
      maxWidth: '600px',
      margin: '0 auto',
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
      textAlign: 'center',
    },
    balanceLabel: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
    },
    balanceAmount: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.success,
    },
    form: {
      maxWidth: '600px',
      margin: '0 auto',
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.xl,
      ...getShadowStyle('md'),
    },
    section: {
      marginBottom: spacing.xl,
    },
    sectionTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.md,
    },
    label: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.sm,
      display: 'block',
    },
    amountInput: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      textAlign: 'center',
      border: `2px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      outline: 'none',
      boxSizing: 'border-box',
    },
    methodSelector: {
      display: 'flex',
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    methodButton: {
      flex: 1,
      padding: spacing.md,
      borderRadius: borderRadius.lg,
      border: `2px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      cursor: 'pointer',
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      transition: 'all 0.2s ease',
    },
    activeMethod: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
      color: colors.primary,
    },
    input: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      fontSize: typography.fontSize.base,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      outline: 'none',
      boxSizing: 'border-box',
    },
    summaryCard: {
      backgroundColor: colors.backgroundSecondary,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.xl,
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    summaryLabel: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    summaryValue: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
    },
    transferButton: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      backgroundColor: colors.primary,
      border: 'none',
      color: colors.white,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      cursor: 'pointer',
      marginTop: spacing.xl,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      transition: 'all 0.2s ease',
    },
    loadingIcon: {
      animation: 'spin 1s linear infinite',
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Transfer Money"
        subtitle="Send money to other users"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          <div style={styles.balanceCard}>
            <div style={styles.balanceLabel}>Available Balance</div>
            <div style={styles.balanceAmount}>{formatCurrency(availableBalance)}</div>
          </div>

          <div style={styles.form}>
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Transfer To</h3>
          <label style={styles.label}>Transfer Type</label>
          <div style={styles.methodSelector}>
            {transferTypes.map((type) => (
              <button
                key={type.id}
                style={{
                  ...styles.methodButton,
                  ...(transferType === type.id ? styles.activeMethod : {}),
                }}
                onClick={() => setTransferType(type.id)}
              >
                <span style={{ marginRight: spacing.sm }}>{type.icon}</span>
                {type.name}
              </button>
            ))}
          </div>

          <label style={styles.label}>
            {transferTypes.find(t => t.id === transferType)?.name || 'Recipient'}
          </label>
          <input
            type={transferType === 'phone' ? 'tel' : transferType === 'email' ? 'email' : 'text'}
            style={styles.input}
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder={
              transferTypes.find(t => t.id === transferType)?.placeholder || ''
            }
          />
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Transfer Details</h3>
          <label style={styles.label}>Amount (KES)</label>
          <input
            type="number"
            style={styles.amountInput}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />

          <label style={styles.label}>Note (Optional)</label>
          <input
            type="text"
            style={styles.input}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note for this transfer"
          />
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Transfer Summary</h3>
          <div style={styles.summaryCard}>
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Transfer Amount</span>
              <span style={styles.summaryValue}>KES {amount || '0.00'}</span>
            </div>
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Transfer Fee</span>
              <span style={styles.summaryValue}>KES 0.00</span>
            </div>
            <div style={{
              ...styles.summaryRow,
              borderTop: `1px solid ${colors.border}`,
              paddingTop: spacing.sm,
              marginTop: spacing.sm,
              marginBottom: 0,
            }}>
              <span style={{
                ...styles.summaryLabel,
                fontWeight: typography.fontWeight.bold,
                color: colors.text,
              }}>
                Total Amount
              </span>
              <span style={{
                ...styles.summaryValue,
                fontSize: typography.fontSize.lg,
                color: colors.primary,
              }}>
                {formatCurrency(parseFloat(amount) || 0)}
              </span>
            </div>
          </div>
        </div>

        <button
          style={{
            ...styles.transferButton,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
          onClick={handleTransfer}
          disabled={loading}
        >
          {loading && <span style={styles.loadingIcon}>🔄</span>}
          {loading ? 'Processing...' : `Transfer ${formatCurrency(parseFloat(amount) || 0)}`}
        </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transfer;